import React, { useState, useMemo, useEffect } from "react";
import PropertyCard from "../../components/LandingComponents/TopSellingProperties/PropertyCard";
import {
  FaSearch,
  FaFilter,
  FaTimes,
  FaSortAmountDown,
  FaSortAmountUp,
} from "react-icons/fa";
import "./AvailableProperties.css";
import api from "../../api/axios";

/* ===================== COMPONENT ===================== */
const AvailableProperties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [priceRange, setPriceRange] = useState([0, 10000000]);
  const [areaRange, setAreaRange] = useState([0, 200000]);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  /* ===================== FETCH DATA ===================== */
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        const response = await api.get("/api/get-properties");

        // Laravel Resources often wrap data in a 'data' key
        const rawData = response.data.data || response.data;

        if (Array.isArray(rawData)) {
          const formattedData = rawData.map(item => {
            // Calculate minimum unit price for the property
            const unitPrices = item.units?.map(u => Number(u.price)) || [];
            const minPrice = unitPrices.length > 0 ? Math.min(...unitPrices) : 0;

            return {
              ...item,
              // Map backend numeric fields for easier filtering/sorting
              price: minPrice,
              area: Number(item.area_sq_ft) || 0,
              units: Number(item.total_units) || 0,
              floors: Number(item.total_floors) || 0,
            };
          });
          setProperties(formattedData);
        } else {
          setError("Invalid data received from server.");
        }
      } catch (err) {
        console.error("Error fetching properties:", err);
        setError("Failed to fetch available properties. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  /* ===================== MEMO ===================== */
  const propertyTypes = useMemo(() => {
    const types = properties.map((p) => p.type?.name).filter(Boolean);
    return ["all", ...new Set(types)];
  }, [properties]);

  const statuses = useMemo(() => {
    const stats = properties.map((p) => p.status?.status_type).filter(Boolean);
    return ["all", ...new Set(stats)];
  }, [properties]);

  const filteredProperties = useMemo(() => {
    return properties.filter((property) => {
      const typeName = property.type?.name || "";
      const statusType = property.status?.status_type || "";
      const locationCity = property.location?.city || "";
      const locationArea = property.location?.area || "";

      const matchesSearch =
        searchQuery === "" ||
        property.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        locationCity.toLowerCase().includes(searchQuery.toLowerCase()) ||
        locationArea.toLowerCase().includes(searchQuery.toLowerCase()) ||
        typeName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType =
        selectedType === "all" || typeName === selectedType;

      const matchesStatus =
        selectedStatus === "all" || statusType === selectedStatus;

      const matchesPrice =
        property.price >= priceRange[0] &&
        property.price <= priceRange[1];

      const matchesArea =
        property.area >= areaRange[0] &&
        property.area <= areaRange[1];

      return (
        matchesSearch &&
        matchesType &&
        matchesStatus &&
        matchesPrice &&
        matchesArea
      );
    }).sort((a, b) => {
      let result = 0;
      if (sortBy === "name") result = a.name.localeCompare(b.name);
      if (sortBy === "price") result = a.price - b.price;
      if (sortBy === "area") result = a.area - b.area;
      if (sortBy === "units") result = a.units - b.units;
      if (sortBy === "floors") result = a.floors - b.floors;
      return sortOrder === "asc" ? result : -result;
    });
  }, [
    properties,
    searchQuery,
    selectedType,
    selectedStatus,
    priceRange,
    areaRange,
    sortBy,
    sortOrder,
  ]);

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedType("all");
    setSelectedStatus("all");
    setPriceRange([0, 10000000]);
    setAreaRange([0, 200000]);
  };

  if (loading) {
    return (
      <div className="ap-container">
        <div className="ap-loading">
          <div className="spinner"></div>
          <p>Loading properties...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ap-container">
        <div className="ap-error">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="btn">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ap-container">
      {/* Header */}
      <div className="ap-header">
        <h1 className="ap-title">Available Properties</h1>
        <p className="ap-subtitle">
          Discover {filteredProperties.length} premium properties
        </p>
      </div>

      {/* Search + Sort */}
      <div className="ap-search-filter-bar">
        <div className="ap-search-container">
          <FaSearch className="ap-search-icon" />
          <input
            className="ap-search-input"
            placeholder="Search by name, location or type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              className="ap-clear-search-btn"
              onClick={() => setSearchQuery("")}
            >
              <FaTimes />
            </button>
          )}
        </div>

        <div className="ap-sort-container">
          <select
            className="ap-sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name">Sort by Name</option>
            <option value="price">Sort by Price</option>
            <option value="area">Sort by Area</option>
            <option value="units">Sort by Units</option>
            <option value="floors">Sort by Floors</option>
          </select>

          <button
            className="ap-sort-order-btn"
            onClick={() =>
              setSortOrder(sortOrder === "asc" ? "desc" : "asc")
            }
          >
            {sortOrder === "asc" ? (
              <FaSortAmountUp />
            ) : (
              <FaSortAmountDown />
            )}
          </button>
        </div>

        <button
          className="ap-filter-toggle-btn"
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter /> Filters
          {showFilters && <span className="ap-filter-badge" />}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="ap-filter-panel">
          <div className="ap-filter-header">
            <h3 className="ap-filter-title">Filters</h3>
            <button className="ap-clear-all-btn" onClick={clearFilters}>
              <FaTimes /> Clear All
            </button>
          </div>

          <div className="ap-filter-grid">
            {/* Type */}
            <div>
              <label className="ap-filter-label">Property Type</label>
              <div className="ap-type-grid">
                {propertyTypes.map((type) => (
                  <button
                    key={type}
                    className={`ap-filter-btn ${selectedType === type ? "active" : ""
                      }`}
                    onClick={() => setSelectedType(type)}
                  >
                    {type === "all" ? "All Types" : type}
                  </button>
                ))}
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="ap-filter-label">Status</label>
              <div className="ap-status-grid">
                {statuses.map((status) => (
                  <button
                    key={status}
                    className={`ap-filter-btn ${selectedStatus === status ? "active" : ""
                      }`}
                    onClick={() => setSelectedStatus(status)}
                  >
                    {status === "all" ? "All Status" : status}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Properties */}
      {filteredProperties.length > 0 ? (
        <div className="ap-properties-grid">
          {filteredProperties.map((property) => (
            <PropertyCard key={property.id} property={property} />
          ))}
        </div>
      ) : (
        <div className="ap-no-results">
          <h3>No Properties Found</h3>
          <button onClick={clearFilters} className="btn">Reset Filters</button>
        </div>
      )}
    </div>
  );
};

export default AvailableProperties;

